'use client'

import { useContext, useState, MouseEventHandler, useEffect } from 'react';
import { faChevronRight, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';
import { ModalContext } from '../../contexts/modal-provider';
import { useUserInfoContext } from '../../contexts/userinfo-context';
import Web3 from 'web3';
import { formatDate } from '../../utils/DateUtils';
import { useTranslations } from 'next-intl';
import { GetTransactions } from '../../services/PaymentSummary/PaymentTransactionService';
import { LandPurchaseContext } from '../../contexts/land-purchase-context';
import Image from 'next/image';
import { useAccount } from 'wagmi';
import { getLandImage, getLandName } from '../../utils/mapHelper';
import { mainNetworkChainId } from '../../constants';
import ShiberseLoader from '../Loader/loader';

type Transaction = {
  id: string;
  coordinates: { x: number; y: number };
  owner: string;
  price: string;
  usd_price: string;
  isBoughtWithShib: boolean;
  shib_usd_price?: string
};


const TabButton = ({
  label,
  onClick,
  active,
}: {
  label: string;
  onClick: MouseEventHandler<HTMLButtonElement>;
  active: boolean;
}) => (
  <button
    className={`px-3 py-1 text-sm text-white lg:text-base ${active ? 'bg-[#F38F2E]' : 'border border-[#F17E0E]'
      } rounded-3xl text-center focus:outline-none`}
    onClick={onClick}
  >
    {label}
  </button>
);

const LandModal = () => {
  const t = useTranslations('Modals');
  const [activeTab, setActiveTab] = useState('my-land-tab');
  const { closeModal } = useContext(ModalContext);
  const {
    lands,
    lockedLeash,
    lockedShiboshi,
    unlockLeash,
    unlockShiboshi,
    claimAllBone,
    claimableBoneAmount,
    refresh,
  } = useUserInfoContext();
  const { address, chain } = useAccount();
  const { isPurchaseSuccessfully } = useContext(LandPurchaseContext);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [isLoadingLands, setIsLoadingLands] = useState(false);

  const handleLeashUnlock = async () => {
    await unlockLeash();
  };

  const handleShiboshiUnlock = async () => {
    await unlockShiboshi();
  };

  const handleClaimAll = async () => {
    await claimAllBone();
  };

  useEffect(() => {
    if (address && chain?.id) {
      const sendRequest = async () => {
        setIsLoadingTransactions(true);
        const res = await GetTransactions({ wallet: address.toLowerCase(), chain: chain?.id });
        setTransactions(res as any);
        setIsLoadingTransactions(false);
      };
      sendRequest();
    }
  }, [address, chain, activeTab]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingLands(true);
      await refresh(); // Call the refresh function and wait for it to complete
      setIsLoadingLands(false);
    };

    fetchData(); // Invoke the async function
  }, []);
  return (
    <div
      id="land-modal"
      tabIndex={-1}
      className={`dark text-md fixed top-5 z-50 mx-auto flex h-screen w-full items-center justify-center overflow-y-auto overflow-x-hidden bg-gray-800 bg-opacity-50 p-4 backdrop-blur md:inset-0`}
    >
      <div className="relative max-h-full w-full max-w-[35rem]">
        <div className="relative rounded-lg bg-white p-5 shadow dark:bg-gray-800 dark:text-white">
          <div className="grid grid-cols-1">
            <h3 className="flex justify-between text-2xl font-medium text-gray-900 dark:text-white">
              {t('my-lands.title')}
              <button
                className="absolute right-4 top-4 z-50 flex p-[6px]"
                onClick={() => closeModal()}
              >
                <FontAwesomeIcon icon={faTimes} color="#747FA6" className="!h-[18px] !w-[18px]" />
              </button>
            </h3>

            <div className="my-5 flex gap-3">
              <TabButton
                label="My Land"
                active={activeTab === 'my-land-tab'}
                onClick={() => setActiveTab('my-land-tab')}
              />
              <TabButton
                active={activeTab === 'transaction-hist-tab'}
                label="Transaction History"
                onClick={() => setActiveTab('transaction-hist-tab')}
              />
              {chain?.id == mainNetworkChainId && (
                <TabButton
                  active={activeTab === 'lock-reward-tab'}
                  label="Locks and Rewards"
                  onClick={() => setActiveTab('lock-reward-tab')}
                />
              )}
            </div>

            {activeTab === 'my-land-tab' && (
              <div
                id="my-land-tab"
                className="custom-scrollbar h-[18rem] overflow-auto bg-gray-800 p-3 w-auto"
              >
                {isLoadingLands ? <div className="flex justify-center items-center h-full">
                  <ShiberseLoader />
                </div> : lands && lands.length > 0 ? (
                  [...lands]?.reverse().map((land, index) => {
                    return (
                      <div
                        key={index}
                        className=" mb-3  flex w-full rounded-2xl border border-slate-700 p-5 shadow-xl"
                      >
                        <div className="mr-3 flex justify-center items-center">
                          <Image src={getLandImage(land?.tierName) || '/images/land.png'} alt="land" width={100} height={100} />
                        </div>

                        <div className="flex w-full flex-col justify-between gap-1">
                          <p className="text-base">SHIB YARD</p>
                          <p className="text-base font-bold tracking-wider">
                            Yard - ({land?.coordinates.x}, {land?.coordinates.y})
                          </p>
                          <p>{getLandName(land.tierName, land)}</p>
                          {/* <Link
                            href={`https://opensea.io/assets/0xEfAEd650f1a94801806BB110019d9B0dc79531A8/${land?.id}`}
                            target="_blank"
                            className="flex items-center justify-end px-3 py-1 text-center text-base text-orange-400"
                          >
                            {t('my-lands.view-opensea')}
                            <FontAwesomeIcon
                              icon={faChevronRight}
                              color="white"
                              className="ml-2 !h-[18px] !w-[18px] text-orange-400"
                            />
                          </Link> */}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <></>
                )}
              </div>
            )}
            {activeTab === 'transaction-hist-tab' && (
              <div
                id="transaction-hist-tab"
                className="custom-scrollbar h-[16rem] overflow-auto bg-gray-800 p-3 text-sm w-auto"
              >
                {isLoadingTransactions ? <div className="flex justify-center items-center h-full">
                  <ShiberseLoader />
                </div> :
                  transactions && transactions.length > 0 &&
                  transactions?.reverse()?.map(({ id, coordinates, owner, price, isBoughtWithShib, usd_price, shib_usd_price }) => (
                    <div key={id}>
                      {/* <p className="text-orange-500 mt-3">{formatDate(t.created_at)}</p> */}
                      <hr className="border-neutral-600 my-2" />
                      <>
                        <div className="grid grid-cols-3 mb-2">
                          <div className="flex flex-col">
                            <p className="font-bold">Mint</p>
                            <p className="font-light">
                              Yard - ({coordinates.x},{coordinates.y})
                            </p>
                          </div>
                          <div className="flex flex-col">
                            <p className="font-bold text-red-600">
                              {isBoughtWithShib ? `${price} SHIB` : `${price} ETH`}
                            </p>
                            {isBoughtWithShib ? (
                              shib_usd_price && <p className="font-light">{shib_usd_price} USD</p>
                            ) : (
                              usd_price && <p className="font-light">{usd_price} USD</p>
                            )}
                          </div>
                          <div className="flex flex-col">
                            <p className="font-bold">From</p>
                            <p className="font-light">{`0x..${owner.slice(-4)}`}</p>
                          </div>
                        </div>
                      </>
                    </div>
                  ))}
              </div>
            )}
            {activeTab === 'lock-reward-tab' && (
              <div
                id="lock-reward-tab"
                className="custom-scrollbar h-[18rem] gap-2 overflow-auto bg-gray-800 p-3 w-auto"
              >
                <div className="block gap-2 lg:grid lg:grid-cols-2">
                  <div className="mb-3 flex h-auto flex-col justify-between gap-2 rounded-2xl border border-slate-700 p-5 shadow-xl">
                    <p className="text-2xl font-bold">
                      {lockedLeash?.amount ? Web3.utils.fromWei(lockedLeash.amount.toString()) : 0}
                    </p>
                    <p className="text-base font-semibold">{t('my-lands.tot-leash-locked')} </p>

                    <button
                      className={`w-fit rounded-3xl px-3 py-1 text-center text-base font-semibold text-white focus:outline-none ${!lockedLeash?.amount || Web3.utils.fromWei(lockedLeash.amount.toString()) === '0'
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-rose-600 hover:bg-rose-700'
                        }`}
                      onClick={() => handleLeashUnlock()}
                      disabled={!lockedLeash?.amount || Web3.utils.fromWei(lockedLeash.amount.toString()) === '0'}
                    >
                      {t('my-lands.unlock-leash-btn')}
                    </button>
                  </div>
                  <div className="mb-3 flex h-auto flex-col justify-between gap-2 rounded-2xl border border-slate-700 p-5 shadow-xl">
                    <p className="text-2xl font-bold">
                      {lockedShiboshi?.ids ? lockedShiboshi.ids?.length : 0}
                    </p>
                    <p className="text-base font-semibold"> {t('my-lands.tot-shiboshi-locked')}</p>
                    <button
                      className={`w-fit rounded-3xl px-3 py-1 text-center text-base font-semibold text-white focus:outline-none ${!lockedShiboshi?.ids || lockedShiboshi.ids.length === 0
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-rose-600 hover:bg-rose-700'
                        }`}
                      onClick={() => handleShiboshiUnlock()}
                      disabled={!lockedShiboshi?.ids || lockedShiboshi.ids.length === 0}
                    >
                      {t('my-lands.unlock-shiboshi-btn')}
                    </button>
                  </div>
                </div>
                <div className="mb-3 flex h-auto flex-col justify-between gap-2 rounded-2xl border border-slate-700 p-5 shadow-xl">
                  <p className="text-2xl font-bold">
                    {claimableBoneAmount
                      ? Number(Web3.utils.fromWei(claimableBoneAmount.toString())).toFixed(2)
                      : 0}
                  </p>
                  <p className="text-base font-semibold"> {t('my-lands.tot-bone-reward')}</p>
                  <button
                    className={`w-fit rounded-3xl px-3 py-1 text-center text-base font-semibold text-white focus:outline-none ${!claimableBoneAmount || Web3.utils.fromWei(claimableBoneAmount.toString()) === '0'
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-green-500 hover:bg-green-600'
                      }`}
                    onClick={() => handleClaimAll()}
                    disabled={!claimableBoneAmount || Web3.utils.fromWei(claimableBoneAmount.toString()) === '0'}
                  >
                    {t('my-lands.claim-bone-btn')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandModal;
